import React from 'react';
import '../../GameEndContent/GameEndPlayerInfo/GameEndPlayerInfo.css';
import {getPlayerHexColor} from "../../../utils/players/getPlayerHexColor";
import {resolveFactionSkinIconPath} from "../../../utils/factions/resolveFactionSkinIconPath";
import {checkIfBotMarkShown} from "../../../utils/bots/checkIfBotMarkShown";
import {getUnitIconDisplayProps} from "../../../utils/factions/succubes/getUnitIconDisplayProps";
import {getNeutralPlayerMock} from "../../../utils/players/getNeutralPlayerMock";
import {getUnitIconClassNameForCombatResults} from "../../../utils/unit/getUnitIconClassNameForCombatResults";
import RankSkinFrame from "../../RankSkinFrame/RankSkinFrame";
import VisibleBotMark from "../../VisibleBotMark/VisibleBotMark";
import {WinPointsStat} from "../../PlayerStat/stats/WinPointsStat";
import UnitIcon from "../../TokensToolbar/NewUnitToolbar/UnitIcon/UnitIcon";
import {MaskedIcon} from "../../coloredIcons/MaskedIcon/MaskedIcon";
import {formatSignedNumber} from "../CityCaptureModal/CityCaptureStatsRow";

const DAMAGE_ICON = '/img/icons/damage.png';
const UNIT_SIZE_ICON = '/img/icons/unit size.png';

/**
 * Иконка вида отрядов со счетчиком: цвет, фракция и скин — как у отряда на
 * поле (см. getUnitIconDisplayProps)
 */
const UnitGroupChip = ({group, players}) => {
  const owner = players.find(({id}) => id === group.ownerId) || getNeutralPlayerMock();

  return (
    <span className="combat-results-player_unit">
      <UnitIcon
        unitType={group.type}
        {...getUnitIconDisplayProps(group, owner, players)}
        className={getUnitIconClassNameForCombatResults(group.type)}
      />
      <span className="combat-results-player_unit-count">×{group.count}</span>
    </span>
  );
};

const UnitGroups = ({label, groups, players, modifier}) => (
  <div className={`combat-results-player_units-group combat-results-player_units-group_${modifier}`}>
    <span className="combat-results-player_units-label">{label}</span>
    {groups.map((group) => (
      <UnitGroupChip
        key={[group.type, group.ownerId, group.factionId, group.capturedFromOwnerId].join('|')}
        group={group}
        players={players}
      />
    ))}
  </div>
);

/**
 * Строка игрока в модалке итогов боя — та же плашка, что в итогах раунда и
 * захвате городов (рамка цвета игрока, портрет со срезанными углами, ник его
 * цветом), а справа: сколько размера своих отрядов он потерял из общего
 * размера до этапа, сколько урона нанес и сколько очков получил. Под ником —
 * какие отряды он потерял и какие уничтожил
 * @param {Object} row - строка buildCombatResultsRows
 * @param {Object[]} players - игроки партии (владельцы уничтоженных отрядов)
 */
const CombatResultsStatsRow = ({row, players}) => {
  const {player, killedUnits, lostUnits, winPoints, damageDealt, sizeLost, sizeBefore} = row;
  const playerHexColor = getPlayerHexColor(player.color);
  const hasUnits = killedUnits.length > 0 || lostUnits.length > 0;
  const isSizeKnown = typeof sizeLost === 'number' && typeof sizeBefore === 'number';
  const sizeLostShare = isSizeKnown && sizeBefore > 0 ? Math.min(1, sizeLost / sizeBefore) : 0;

  return (
    <div
      className={`game-end-player-info combat-results-player${hasUnits ? ' combat-results-player_with-units' : ''}`}
      style={{borderLeftColor: playerHexColor}}
    >
      <div className="game-end-player-icon combat-results-player_icon">
        <img
          className={'side-menu_faction-icon'}
          src={resolveFactionSkinIconPath(player.factionId, player.selectedSkin)}
          alt={player.factionId}
        />
        <RankSkinFrame skinId={player.selectedSkin}/>
        {checkIfBotMarkShown(player) && <VisibleBotMark player={player}/>}
      </div>

      <div className="game-end-player-name combat-results-player_name" style={{color: playerHexColor}}>
        <span className="game-end-player-name-text" title={player.name}>{player.name}</span>
      </div>

      <div
        className="combat-results-player_size"
        title={isSizeKnown ? `Потеряно размера отрядов: ${sizeLost} из ${sizeBefore}` : undefined}
      >
        {isSizeKnown ? (
          <>
            <span className="combat-results-player_size-values">
              <span className={sizeLost > 0 ? 'combat-results-player_delta-down' : 'combat-results-player_muted'}>
                {formatSignedNumber(-sizeLost)}
              </span>
              <span className="combat-results-player_size-total">
                /{sizeBefore}
                {/* Цвет — как у числа рядом: маска заливается currentColor */}
                <MaskedIcon
                  className={'combat-results-player_size-icon'}
                  src={UNIT_SIZE_ICON}
                  color={'currentColor'}
                />
              </span>
            </span>
            <span className="combat-results-player_size-bar">
              <span style={{width: `${Math.round(sizeLostShare * 100)}%`}}/>
            </span>
          </>
        ) : null}
      </div>

      <div
        className={`combat-results-player_damage${damageDealt ? '' : ' combat-results-player_muted'}`}
        title={typeof damageDealt === 'number' ? `Нанесено урона: ${damageDealt}` : undefined}
      >
        {typeof damageDealt === 'number' ? (
          <>
            <img src={DAMAGE_ICON} alt={''}/>
            {damageDealt}
          </>
        ) : null}
      </div>

      <div className="combat-results-player_points" title={'Очки за бой'}>
        <WinPointsStat
          className={'combat-results-player_points-stat'}
          text={formatSignedNumber(winPoints)}
          isDarkColor={true}
          noShrinkOnMobile={true}
        />
      </div>

      {hasUnits && (
        <div className="combat-results-player_units">
          {lostUnits.length > 0 && (
            <UnitGroups label={'Потеряно'} groups={lostUnits} players={players} modifier={'lost'}/>
          )}
          {killedUnits.length > 0 && (
            <UnitGroups label={'Уничтожено'} groups={killedUnits} players={players} modifier={'killed'}/>
          )}
        </div>
      )}
    </div>
  );
};

export default CombatResultsStatsRow;
